import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import StarRatingInput from "@/components/StarRatingInput/StarRatingInput";
import ReviewRequest from "@/model/ReviewRequest";
import "@/components/ReviewForm/ReviewForm.scss";

const MIN_LENGTH = 20;
const MAX_LENGTH = 2000;

interface ReviewFormProps {
  initialRating?: number;
  initialComment?: string;
  submitLabel: string;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (request: ReviewRequest) => void;
}

function ReviewForm({
  initialRating = 0,
  initialComment = "",
  submitLabel,
  isSubmitting,
  error,
  onSubmit,
}: ReviewFormProps) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(initialRating);
  const [comment, setComment] = useState(initialComment);
  const [validationError, setValidationError] = useState<string | null>(null);

  const length = comment.trim().length;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (rating < 1) {
      setValidationError(t("reviews.form.ratingRequired"));
      return;
    }
    if (length < MIN_LENGTH) {
      setValidationError(
        t("reviews.form.commentTooShort", { count: MIN_LENGTH })
      );
      return;
    }
    setValidationError(null);
    onSubmit({ rating, comment: comment.trim() });
  };

  const shownError = validationError ?? error;

  return (
    <form className="review-form" onSubmit={handleSubmit} noValidate>
      <StarRatingInput
        value={rating}
        onChange={setRating}
        legend={t("reviews.form.ratingLegend")}
      />

      <div className="review-form__field">
        <label htmlFor="review-comment">{t("reviews.form.commentLabel")}</label>
        <textarea
          id="review-comment"
          value={comment}
          maxLength={MAX_LENGTH}
          rows={7}
          placeholder={t("reviews.form.commentPlaceholder")}
          onChange={(event) => setComment(event.target.value)}
        />
        <span className="review-form__counter">
          {length < MIN_LENGTH
            ? t("reviews.form.charactersToGo", { count: MIN_LENGTH - length })
            : `${comment.length} / ${MAX_LENGTH}`}
        </span>
      </div>

      {shownError && (
        <p className="review-form__error" role="alert">
          {shownError}
        </p>
      )}

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t("reviews.form.submitting") : submitLabel}
      </button>
    </form>
  );
}

export default ReviewForm;
export type { ReviewFormProps };
